import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type {
  TemplateFormSectionCopy,
  TemplateFormTranslations,
} from "@/lib/i18n/types/documents-sections/templateForm";
import type {
  TemplateFormFieldDef,
  TemplateFormFieldId,
  TemplateFormSectionDef,
} from "../../constants/templateForms";
import type { DocumentTemplateId } from "../../constants/documentTemplates";
import type { SavedTemplateForm } from "../../hooks/useSavedTemplateForms";
import type { SavedFormsProjectChoice, SavedFormsState } from "./SavedFormPicker";
import {
  attachmentsValue,
  peopleValue,
  type TemplateFormValue,
  type TemplateFormValues,
} from "../../utils/templateForm";
import { AttachmentsField } from "./AttachmentsField";
import { PersonRowsField, type TemplateFormMembers } from "./PersonRowsField";
import { TemplateFormField } from "./TemplateFormField";

interface TemplateFormSectionProps {
  section: TemplateFormSectionDef;
  copy: TemplateFormTranslations;
  values: TemplateFormValues;
  members: TemplateFormMembers;
  buildingId: string;
  projectId: string | null;
  onChange: (id: TemplateFormFieldId, value: TemplateFormValue) => void;
  /** The already-filed forms each importable field can pull from, keyed by the
   *  template its schema names as the source. */
  importSources: Partial<Record<DocumentTemplateId, SavedFormsState>>;
  /** Set only at building scope — see `SavedFormsProjectChoice`. */
  savedFormsProject?: SavedFormsProjectChoice;
  /** Import pulls one field's content out of the form the user picked. */
  onImport: (field: TemplateFormFieldDef, form: SavedTemplateForm) => void;
}

/**
 * One section of a template form: an uppercase label, an optional helper line,
 * then its fields. Plain fields pair two-per-row inside a readable measure;
 * the person and attachment blocks need the full width and sit below them.
 */
export function TemplateFormSection({
  section,
  copy,
  values,
  members,
  buildingId,
  projectId,
  onChange,
  importSources,
  savedFormsProject,
  onImport,
}: TemplateFormSectionProps) {
  const sectionCopy = copy.sections[section.id];
  const inlineFields = section.fields.filter(
    (field) => field.type !== "people" && field.type !== "attachments"
  );
  const blockFields = section.fields.filter(
    (field) => field.type === "people" || field.type === "attachments"
  );

  // Attachments and Parties carry the tallest blocks on the form — a file list
  // and two signature tables — and both are optional on a form whose point is
  // the works description. They start folded so the form opens on the fields
  // that have to be filled, with Save in reach.
  const collapsible = section.id === "attachments" || section.id === "parties";
  const [open, setOpen] = useState(false);
  const body = (
    <>
      {inlineFields.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {inlineFields.map((field) => (
            <TemplateFormField
              key={field.id}
              field={field}
              copy={copy}
              value={values[field.id] ?? ""}
              onChange={(value) => onChange(field.id, value)}
              importSources={
                field.importFrom ? importSources[field.importFrom.templateId] : undefined
              }
              savedFormsProject={savedFormsProject}
              onImport={(form) => onImport(field, form)}
            />
          ))}
        </div>
      )}

      {blockFields.map((field) => (
        <div key={field.id} role="group" aria-label={copy.fields[field.id]} className="mt-4">
          {field.type === "people" ? (
            <PersonRowsField
              copy={copy}
              people={peopleValue(values, field.id)}
              members={members}
              withCertificate={Boolean(field.withCertificate)}
              onChange={(people) => onChange(field.id, people)}
            />
          ) : (
            <AttachmentsField
              copy={copy}
              attachments={attachmentsValue(values, field.id)}
              buildingId={buildingId}
              projectId={projectId}
              onChange={(attachments) => onChange(field.id, attachments)}
            />
          )}
        </div>
      ))}
    </>
  );

  if (!collapsible) {
    return (
      <section className="border-t pt-6 first:border-t-0 first:pt-0">
        <SectionHeading copy={sectionCopy} />
        {body}
      </section>
    );
  }

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <section className="border-t pt-6 first:border-t-0 first:pt-0">
        <CollapsibleTrigger className="group flex w-full items-start gap-2 text-left">
          <ChevronDown
            aria-hidden
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90"
          />
          <SectionHeading copy={sectionCopy} />
        </CollapsibleTrigger>
        <CollapsibleContent>{body}</CollapsibleContent>
      </section>
    </Collapsible>
  );
}

function SectionHeading({ copy }: { copy: TemplateFormSectionCopy }) {
  return (
    <div className="min-w-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {copy.title}
      </h3>
      {copy.help && <p className="mt-1 text-xs text-muted-foreground">{copy.help}</p>}
    </div>
  );
}
